import { AxesPrimitive, DataChangeStruct, Primitive, PythaEngineControl, PythaEventDataChange, PythaEventName, PythaEventObject, PythaEventSetting, PythaEventZoomAll, SETTING_KEY, UpdateType } from 'pytha2';
import './index.less';
import { useEffect, useState, useRef, useContext } from 'react';
import { EngineContext, NavigationContext, PositionContext } from '@/App';
import { message, Spin } from 'antd';
import Draw from '../draw/draw';
import { Button, ContextMenuWrapper, IconButton, Input, Modal } from 'bimui';
import { Matrix4, Vector3 } from 'three';
import { createAxisNetPrimitive, findPrimitivesByUuids } from '@/declare/api/geometry/primitive';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { EventGraphGenGridPoint } from '@/plugin/event-graph/EGGenGridPoint';
import { EventGraphAxisLine } from '@/plugin/event-graph/EGAxisLine';
import { EventGraphGenGridNumberAndMark } from '@/plugin/event-graph/EGGenGridNumberAndMark';
import { getSpecialPrimitiveLayerUuid } from '@/utils/layer-utils';
import { LayerType } from '@/declare/enums/layer-type';
import { Storey } from '@/declare/interface/project/storey';
import classNames from 'classnames';
import { EventGraphQuickGenAxisNet } from '@/plugin/event-graph/EGQuickGenAxisNet';
import { EventGraphAlignAxisNet } from '@/plugin/event-graph/EGAlignAxisNet';
import { BaseUtils, Point } from 'tnbase';
import localStore from '@/store/local-store';
import { ReactComponent as CloseSvg } from 'bimui/dist/c1/icons/cancel.svg'
import PositionPanel from '@/layout/graphic/axis-net/position';


interface Grid {
    bay?: string // 开间
    deep?: string // 进深
    x?: string // X坐标
    y?: string // Y坐标
}


export default function DrawAxisNet() {

    /**
    * 路由参数
    */
    const { projectUuid } = useParams();
    /**
    * 引擎上下文，用于管理全局的引擎实例。
    */
    const engineContext = useContext(EngineContext);
    /**
    * 页签上下文
    */
    const navigateContext = useContext(NavigationContext);
    /**
    * 页签上下文
    */
    const positionContext = useContext(PositionContext);
    /**
     * 正在编辑的楼层
     */
    const activeStoreyRef = useRef<Storey>(null);
    /**
     * 快速生成的开间和进深
     */
    const [grid, setGrid] = useState<Grid>({ ...localStore.axisNetGrid });
    /**
     * 路由地址
     */
    const location = useLocation();
    /**
     * 路由导航
     */
    const navigate = useNavigate();

    useEffect(() => {
        // getSpecialPrimitiveLayerUuid(projectUuid, LayerType.AXIS_NET, engineContext, '#E54242').then((layerUuid: string) => {
        //     engineContext.setCurrentLayerUuid(layerUuid);
        // })
        // 关闭页签，清空上下文
        return () => {
            // navigateContext.removeTab(location.pathname);
            positionContext.clear();
        }
    }, [projectUuid])

    useEffect(() => {
        localStore.axisNetGrid = { ...grid };
    }, [grid])

    useEffect(() => {
        if (!projectUuid || projectUuid == 'undefined') return;
        navigateContext.registTab({
            key: location.pathname,
            value: `定位`,
        })
        navigateContext.setActiveTabKey(location.pathname)
    }, [projectUuid])

    useEffect(() => {
        if (!engineContext?.engine) return;
        if (!engineContext?.engine?.storage) return;
        // 设置当前正在编辑的层，那么就要冻结其他的层，并且
        activeStoreyRef.current = positionContext?.activeStorey;

        setTimeout(() => {
            Object.keys(engineContext?.engine?.storage?.primitiveStore?.primitiveDict).forEach(key => {
                let primitive = engineContext.engine.storage.primitiveStore.primitiveDict[key];

                // if (!!(primitive as AxisNetPrimitive)?.parentUuid && (primitive as AxisNetPrimitive)?.axisNetUuid == activeStoreyRef.current?.axisNetUuid) {
                if (!!primitive?.parentUuid && primitive?.parentUuid == activeStoreyRef.current?.axisNetUuid) {
                    primitive.lock = false;
                    primitive.needRedraw = true;
                } else {
                    primitive.lock = true;
                    primitive.needRedraw = true;
                }
            })
            // 渲染
            engineContext.engine.eventMediator.renderHandler.render(true, UpdateType.ALL);
        }, 100)

        if (!positionContext?.activeStorey) return;
        // return;
        let lengthUnitRatio = engineContext?.engine?.storage?.coordinateStore?.lengthUnitRatio;
        const axes = new AxesPrimitive();
        // axes.localModelMatrix.setPosition(new Vector3(0, 0, positionContext?.activeStorey?.elevation * 1000));
        axes.localModelMatrix.setPosition(new Vector3(0, 0, positionContext?.activeStorey?.elevation));
        axes.setByMatrix(
            axes.localModelMatrix,
            engineContext?.engine?.storage.viewportStore.viewportItems[engineContext?.engine?.storage.viewportStore.currentViewportKey].cameraEdge
        );
        // 创建一个ucs
        let pythaEvent = new PythaEventSetting();
        pythaEvent.data = { key: SETTING_KEY.AXES, value: axes }
        let pythaEventKeep = new PythaEventSetting();
        pythaEventKeep.data = { key: SETTING_KEY.AXES_KEEP, value: null }
        engineContext?.engine?.eventMediator?.emitEvent(pythaEvent);
        engineContext?.engine?.eventMediator?.emitEvent(pythaEventKeep);
        // 修改工作面
        // engineContext?.engine?.storage?.coordinateStore?.workPlane.setP(new Point(0, 0, positionContext?.activeStorey?.elevation * 1000));
        engineContext?.engine?.storage?.coordinateStore?.workPlane.setP(new Point(0, 0, positionContext?.activeStorey?.elevation));

    }, [engineContext?.engine, positionContext?.activeStorey, positionContext?.renderFlag])

    /**
    * 初始化模型数据
    * - 从后端获取与当前图形关联的模型数据。
    * - 将模型数据加载到引擎中。
    * 
    * @param engine Pytha 引擎实例。
    * @returns Promise 对象。
    */
    const initModels = (engine: PythaEngineControl, layer) => {
        return new Promise((resolve, reject) => {
            let uuids = positionContext?.axisNetUuids || [];
            findPrimitivesByUuids(uuids).then(res => {
                let primitives = [];
                let jsonList = res.data || [];
                for (let json of jsonList) {
                    // 这里需要给子乘父的矩阵
                    let mat = new Matrix4();
                    mat.elements = json.properties.localModelMatrix;

                    json?.properties?.children?.forEach(item => {
                        let primitive: Primitive = new Primitive(item.type);
                        primitive.fromJson(item);

                        if (primitive.localModelMatrix == null) {
                            primitive.localModelMatrix = new Matrix4();
                        }
                        if (primitive.worldModelMatrix == null) {
                            primitive.worldModelMatrix = new Matrix4();
                        }
                        // 乘父的矩阵 也就是乘层高
                        primitive.localModelMatrix.premultiply(mat);
                        primitive.worldModelMatrix.copy(primitive.localModelMatrix);
                        if (primitive.worldModelMatrixInverse == null) {
                            primitive.worldModelMatrixInverse = new Matrix4();
                        }
                        primitive.worldModelMatrixInverse.copy(primitive.worldModelMatrix).invert();

                        if (!!json.properties.uuid
                            && json.properties.uuid == activeStoreyRef.current?.axisNetUuid) {
                            primitive.lock = false;
                            primitive.needRedraw = true;
                        } else {
                            primitive.lock = true;
                            primitive.needRedraw = true;
                        }

                        primitive.layerUuid = layer?.uuid;

                        if (primitive.colorByLayer) {
                            // 直接设置为轴网图层的颜色
                            Object.keys(primitive.renderItemDict).forEach(key => {
                                let item = primitive.renderItemDict[key];
                                item.mat?.color.set(layer?.color || '#E54242');
                            })
                        }

                        primitives.push(primitive);
                    })
                }
                if (engine) {
                    // 先清空图元
                    Object.keys(engine?.storage?.primitiveStore?.primitiveDict || {})?.forEach(uuid => {
                        const primitive = engineContext?.engine?.storage?.primitiveStore?.primitiveDict[uuid];
                        primitive.needDelete = true;
                    });
                    engine?.eventMediator?.renderHandler?.render(true, UpdateType.ALL);

                    let dataChange = new PythaEventDataChange({ add_primitives: primitives });
                    setTimeout(() => {
                        engine?.eventMediator?.emitEvent(dataChange);
                        setTimeout(() => {
                            const pythaEvent = new PythaEventZoomAll();
                            pythaEvent.data = {};
                            engine?.eventMediator?.emitEvent(pythaEvent);
                            // setLoading(false); // 数据加载完成后隐藏 Loading
                        }, 100);
                    }, 0)
                } else {
                    console.log("engineContext.engine is null");
                }
            })
        });
    }

    /**
     * 注册事件到引擎、加载图形
     */
    useEffect(() => {
        if (!engineContext?.engine) return;
        if (!engineContext?.engine?.storage) return;
        // 注册轴网相关的事件图
        registerAxisNetGraph();
        getSpecialPrimitiveLayerUuid(projectUuid, LayerType.AXIS_NET, engineContext, '#E54242').then((layerUuid: string) => {
            engineContext.setCurrentLayerUuid(layerUuid);
            let layer = engineContext?.engine?.storage?.layerStore?.layers?.find(item => item.uuid === layerUuid);
            initModels(engineContext?.engine, layer);
        })

        const unsubscribe = engineContext?.engine?.eventMediator?.noticeHandler?.onNotice?.subscribe((sender, event) => {
            if (event.cmd === PythaEventName.DATA_CHANGE) {
                let data = event?.data as DataChangeStruct;
                if (!data) return;
                if (data.add_primitives && data.add_primitives.length > 0) {
                    // 命令会修改图元后直接发信号，还未进行网格划分和渲染，会影响生成网点获取当前线
                    setTimeout(() => {
                        finish();
                    }, 100)
                }
                if (data.delete_primitives && data.delete_primitives.length > 0) {
                    // 命令会修改图元后直接发信号，还未进行网格划分和渲染，会影响生成网点获取当前线
                    setTimeout(() => {
                        finish();
                    }, 100)
                }
            }
        })

        return () => {
            unsubscribe?.();
        }
    }, [engineContext?.engine, positionContext?.axisNetUuids, positionContext?.renderFlag]);

    /**
     * 注册轴网相关的事件图
     */
    const registerAxisNetGraph = () => {
        engineContext?.engine?.storage.registerStore.registEventGraph(EventGraphGenGridPoint.cmd, EventGraphGenGridPoint);
        engineContext?.engine?.storage.registerStore.registEventGraph(EventGraphAxisLine.cmd, EventGraphAxisLine);
        engineContext?.engine?.storage.registerStore.registEventGraph(EventGraphGenGridNumberAndMark.cmd, EventGraphGenGridNumberAndMark);
        engineContext?.engine?.storage.registerStore.registEventGraph(EventGraphQuickGenAxisNet.cmd, EventGraphQuickGenAxisNet);
        engineContext?.engine?.storage.registerStore.registEventGraph(EventGraphAlignAxisNet.cmd, EventGraphAlignAxisNet);
    }

    /**
     * 激活引擎事件图
     * @param cmd 事件图cmd
     */
    const activeAxisNetGraph = (cmd: string) => {
        if (!engineContext?.engine) return;
        engineContext.emitCommand(cmd);
    }

    /**
     * 轴网修改
     * @param value 
     * @param field 
     */
    const gridChange = (value: string, field: string) => {
        let newGrid = { ...grid };
        newGrid[field] = value;
        setGrid(newGrid);
    }

    /**
     * 轴网失焦
     * @param field 
     * @returns 
     */
    const gridBlur = (field: string) => {
        let value: string = grid[field];
        if (!value) {
            return;
        }
        // 只保留数字、逗号和星号
        let newValue = value?.replace("，", ',').replace(/[^0-9,*]/g, '');
        if (newValue.endsWith(',')) {
            newValue = newValue.slice(0, -1);
        }
        if (value === newValue) {
            return;
        }
        let newGrid = { ...grid };
        newGrid[field] = newValue;
        setGrid(newGrid);
    }

    /**
     * 快速生成轴网
     */
    const quickGenAxisNet = (e) => {
        e.stopPropagation();
        e.preventDefault();
        // 判断轴网数据非空
        let gridFlag = Object.values(grid)?.every((value) => value == undefined || value === '' || value?.trim() === '');
        if (gridFlag) {
            message.warning('轴网数据至少要有一项不为空');
            return;
        }
        if (!engineContext?.engine) return;
        if (engineContext?.engine?.isCmdActive) {
            message.warning('当前存在激活事件，无法生成');
            return;
        }
        engineContext.emitCommand("QUICKGENAXISNET");
        let pythaEvent = new PythaEventObject();
        pythaEvent.data = grid;
        engineContext.engine.eventMediator?.emitEvent(pythaEvent);
    }


    /**
     * 自动完成
     */
    const finish = () => {
        if (!activeStoreyRef.current) {
            message.warning("未选择编辑的楼层");
            return;
        }
        if (!engineContext?.engine?.isCmdActive) {
            activeAxisNetGraph("GENGRIDPOINT");
        }

        // 尝试添加一个轴网图元
        let axisNet = new Primitive("AxisNet");
        if (!!activeStoreyRef.current?.axisNetUuid) {
            axisNet.uuid = activeStoreyRef.current.axisNetUuid;
        }

        // 筛选未冻结的图元作为轴网的子图元
        Object.keys(engineContext?.engine?.storage?.primitiveStore?.primitiveDict)?.forEach(uuid => {
            const primitive = engineContext?.engine?.storage?.primitiveStore?.primitiveDict[uuid];
            if (!primitive.lock) {
                const priClone = primitive.clone();
                if (priClone.localModelMatrix) {
                    priClone.localModelMatrix.elements[14] = 0;
                }
                priClone?.geos?.forEach(geo => {
                    geo.transformMatrix.elements[14] = 0;
                })
                axisNet.addChildPrimitive(priClone);
            }
        });

        const matrix = new Matrix4();
        // 这里实际应该是当前层的标高
        matrix.set(
            1, 0, 0, 0,
            0, 1, 0, 0,
            // 0, 0, 1, (activeStoreyRef.current?.elevation * 1000 || 0),
            0, 0, 1, (activeStoreyRef.current?.elevation || 0),
            0, 0, 0, 1
        )

        // 变换矩阵
        axisNet.localModelMatrix = matrix;

        // 创建或找到一个轴网图层，然后轴网图元属于这个图层
        getSpecialPrimitiveLayerUuid(projectUuid, LayerType.AXIS_NET, engineContext, '#E54242').then((layerUuid: string) => {
            axisNet.layerUuid = layerUuid;
            let param = {
                projectUuid: projectUuid,
                storeyUuid: activeStoreyRef.current?.uuid
            }
            createAxisNetPrimitive(axisNet.toJson(), param).then(res => {
                // message.success("编辑成功");
                // navigateContext.removeTab(location.pathname);
                // navigate(-1);
            }).catch(err => {
                message.error(err);
            })
        })
    }

    return (
        <div className='draw-axis-net-container'>
            <PositionPanel />
            <div className='draw-axis-net-tool' style={{ display: positionContext.fastGenVisible ? 'flex' : 'none' }}>
                <div className='tool-axis-net-container'>
                    <div className='head-wrap'>
                        <div className={classNames({
                            'head-block': true,
                            'head-block-active': true,
                        })}>直线</div>
                    </div>
                    <IconButton wrapClassName='close-icon' small icon={<CloseSvg />} onClick={() => {
                        positionContext.setFastGenVisible(false);
                    }}></IconButton>
                    <div className="quick-wrap">
                        <Input
                            label='开间'
                            defaultValue={null}
                            value={grid?.bay || ''}
                            onChange={(e) => gridChange(e.target.value, 'bay')}
                            onBlur={() => gridBlur('bay')}
                            placeholder='开间尺寸*开间数，不同开间逗号隔开'
                        />
                        <Input
                            label='进深'
                            value={grid?.deep || ''}
                            onChange={(e) => gridChange(e.target.value, 'deep')}
                            onBlur={() => gridBlur('deep')}
                            placeholder='进深尺寸*进深数，不同进深逗号隔开'
                        />
                        <div className='choose-row-group'>
                            <Input
                                label='坐标'
                                value={grid?.x || ''}
                                onChange={(e) => gridChange(e.target.value, 'x')}
                                onBlur={() => gridBlur('x')}
                                placeholder='X'
                            />
                            <Input
                                value={grid?.y || ''}
                                onChange={(e) => gridChange(e.target.value, 'y')}
                                onBlur={() => gridBlur('y')}
                                placeholder='Y'
                            />
                            <Input
                                // 这里是正在编辑的标高 
                                // value={(positionContext?.activeStorey?.elevation * 1000 || '0').toString()}
                                value={(positionContext?.activeStorey?.elevation / engineContext?.engine?.storage?.coordinateStore?.lengthUnitRatio || '0').toString()}
                                placeholder='选择楼层'
                                disabled={true}
                            />
                        </div>
                    </div>
                    <div className='btn-wrap'>
                        <Button primary small onClick={quickGenAxisNet}>{(!grid?.x && !grid?.y) ? '拾取' : '放置'}</Button>
                        <Button danger small onClick={() => {
                            setGrid({});
                        }}>清空</Button>
                    </div>
                </div>
            </div>
            <Draw />
        </div >

    )
}